import { Component, computed, input, signal } from '@angular/core';
import { FormArray, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { TabsModule } from 'primeng/tabs';
import { CustomPricingDialogComponent } from './custom-pricing-dialog/custom-pricing-dialog.component';
import { CustomPricingListComponent } from './custom-pricing-list/custom-pricing-list.component';
import {
    TierPricingEditorComponent,
    CustomFormGroup,
    CustomPricing,
    PricingEntity,
    TierFormGroup,
    createCustomGroup,
    customGroupToValue,
    tierGroupToValue,
} from '../shared';

@Component({
    selector: 'app-pricing-configurator',
    templateUrl: './pricing-configurator.component.html',
    styleUrls: ['./pricing-configurator.component.css'],
    imports: [
        ReactiveFormsModule,
        TranslateModule,
        ButtonModule,
        TabsModule,
        TierPricingEditorComponent,
        CustomPricingDialogComponent,
        CustomPricingListComponent,
    ],
})
export class PricingConfiguratorComponent {
    readonly pricingList = input.required<FormArray<TierFormGroup>>();
    readonly customPricingList = input.required<FormArray<CustomFormGroup>>();
    readonly pricingEntities = input.required<PricingEntity[]>();

    readonly activeTab = signal<string | number | undefined>('default');
    readonly dialogVisible = signal(false);
    readonly editingIndex = signal<number | null>(null);

    readonly defaultTierValues = computed(() => this.pricingList().controls.map((group) => tierGroupToValue(group)));

    readonly editingValue = computed<CustomPricing | null>(() => {
        const index = this.editingIndex();
        if (index === null) {
            return null;
        }
        const group = this.customPricingList().at(index);
        return group ? customGroupToValue(group) : null;
    });

    readonly availableForCustom = computed(() => {
        const used = new Set(this.customPricingList().controls.map((group) => group.controls.relatedEntityId.value));
        const editingId = this.editingIndex() !== null ? this.customPricingList().at(this.editingIndex()!).controls.relatedEntityId.value : null;
        return this.pricingEntities().filter((entity) => !used.has(entity.id) || entity.id === editingId);
    });

    openAddDialog(): void {
        this.editingIndex.set(null);
        this.dialogVisible.set(true);
    }

    openEditDialog(index: number): void {
        this.editingIndex.set(index);
        this.dialogVisible.set(true);
    }

    closeDialog(): void {
        this.dialogVisible.set(false);
        this.editingIndex.set(null);
    }

    saveCustom(custom: CustomPricing): void {
        const list = this.customPricingList();
        const index = this.editingIndex();
        const group = createCustomGroup(custom);
        if (index !== null) {
            list.setControl(index, group);
        } else {
            list.push(group);
        }
        this.closeDialog();
    }

    removeCustom(index: number): void {
        this.customPricingList().removeAt(index);
    }
}
